import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
    ShieldCheck,
    Check,
    XCircle,
    Trash2,
    LogOut,
    Loader2,
    RefreshCw,
    Users as UsersIcon,
    MessageSquare,
    BarChart3,
    Plus,
    ArrowLeft,
    FileText,
    Home as HomeIcon,
} from "lucide-react";
import { useAuth, TOKEN_KEY } from "@/hooks/useAuth";
import AuthModal from "@/components/AuthModal";
import ArticlesTab from "@/components/ArticlesTab";
import HomeCopyTab from "@/components/HomeCopyTab";
import { APP_VERSION, PUBLISHED_ON } from "@/version";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];

const threadLabel = {
    "the-equity-nation": "The Equity Nation",
    "our-economy-not-theirs": "Our Economy, Not Theirs",
    "en-concept": "EN — Concept",
    "podcast-monopoly": "Podcast · Monopoly",
};

const formatError = (d) =>
    !d
        ? "Something went wrong."
        : typeof d === "string"
            ? d
            : Array.isArray(d)
                ? d.map((e) => e?.msg || "").filter(Boolean).join(" ")
                : d?.msg || "Something went wrong.";

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-CA");
    } catch {
        return iso;
    }
};

const roleRank = { member: 0, moderator: 1, admin: 2, super_admin: 3 };

function StatsCard({ token }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/stats/visits`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!cancel) setData(res.data);
            } catch (e) {
                // stats unavailable
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => {
            cancel = true;
        };
    }, [token]);

    const maxDay = useMemo(
        () => (data ? Math.max(1, ...data.last_7_days.map((d) => d.count)) : 1),
        [data],
    );

    if (loading) {
        return (
            <div className="flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                <Loader2 size={14} className="animate-spin" />
                Loading stats
            </div>
        );
    }
    if (!data) return null;

    return (
        <div
            data-testid="stats-card"
            className="border border-[color:var(--charcoal)] p-6 md:p-8 bg-[color:var(--cloud)]"
        >
            <div className="flex items-center gap-2 mb-6">
                <BarChart3 size={14} strokeWidth={1.6} />
                <span className="font-mono text-[12px] uppercase tracking-[0.26em]">
                    Visitors (PIPEDA-friendly, no cookies)
                </span>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-8">
                <div>
                    <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1">
                        Today
                    </div>
                    <div
                        data-testid="stat-today"
                        className="font-display text-[2rem] md:text-[2.6rem] leading-none tabular-nums"
                    >
                        {data.today}
                    </div>
                </div>
                <div>
                    <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1">
                        Total visits
                    </div>
                    <div
                        data-testid="stat-total"
                        className="font-display text-[2rem] md:text-[2.6rem] leading-none tabular-nums"
                    >
                        {data.total}
                    </div>
                </div>
                <div>
                    <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1">
                        Unique to date
                    </div>
                    <div
                        data-testid="stat-unique"
                        className="font-display text-[2rem] md:text-[2.6rem] leading-none tabular-nums"
                    >
                        {data.unique_total}
                    </div>
                </div>
            </div>
            <div>
                <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-2">
                    Last 7 days
                </div>
                <div className="flex items-end gap-2 h-24">
                    {data.last_7_days.map((d) => (
                        <div
                            key={d.date}
                            className="flex-1 flex flex-col items-center gap-1"
                        >
                            <div className="w-full flex items-end h-full">
                                <div
                                    className="w-full bg-[color:var(--charcoal)]"
                                    style={{
                                        height: `${Math.max(3, (d.count / maxDay) * 100)}%`,
                                    }}
                                    title={`${d.date}: ${d.count}`}
                                />
                            </div>
                            <div className="font-mono text-[11px] text-[color:var(--slate)]">
                                {d.date.slice(5)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function UsersTab({ token, me }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [error, setError] = useState(null);
    const [roleFilter, setRoleFilter] = useState("all");
    const [form, setForm] = useState({
        email: "",
        name: "",
        nickname: "",
        password: "",
        role: "moderator",
    });
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const counts = useMemo(() => {
        const c = { all: users.length, member: 0, moderator: 0, admin: 0, super_admin: 0 };
        for (const u of users) if (c[u.role] !== undefined) c[u.role] += 1;
        return c;
    }, [users]);
    const visibleUsers = useMemo(
        () => (roleFilter === "all" ? users : users.filter((u) => u.role === roleFilter)),
        [users, roleFilter],
    );

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/users`, { headers });
            setUsers(res.data || []);
        } catch (e) {
            setError(formatError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const canActOn = (target) => {
        if (target.role === "super_admin") return false;
        if (target.id === me?.id) return false;
        // Admin cannot demote/delete a fellow admin unless super-admin
        if (target.role === "admin" && me?.role !== "super_admin") return false;
        return true;
    };

    const changeRole = async (id, role) => {
        setError(null);
        try {
            await axios.post(
                `${API}/admin/users/${id}/role`,
                { role },
                { headers },
            );
            refresh();
        } catch (e) {
            setError(formatError(e.response?.data?.detail));
        }
    };

    const deleteUser = async (id) => {
        setError(null);
        try {
            await axios.delete(`${API}/admin/users/${id}`, { headers });
            refresh();
        } catch (e) {
            setError(formatError(e.response?.data?.detail));
        }
    };

    const createUser = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            await axios.post(`${API}/admin/users`, form, { headers });
            setShowCreate(false);
            setForm({ email: "", name: "", nickname: "", password: "", role: "moderator" });
            refresh();
        } catch (e2) {
            setError(formatError(e2.response?.data?.detail));
        }
    };

    return (
        <div data-testid="users-tab">
            <div
                className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6"
                data-testid="users-counts"
            >
                {[
                    { key: "all", label: "Total" },
                    { key: "member", label: "Members" },
                    { key: "moderator", label: "Moderators" },
                    { key: "admin", label: "Admins" },
                    { key: "super_admin", label: "Super admin" },
                ].map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setRoleFilter(t.key)}
                        data-testid={`users-filter-${t.key}`}
                        aria-pressed={roleFilter === t.key}
                        className={`text-left p-3 border transition-colors ${
                            roleFilter === t.key
                                ? "border-[color:var(--charcoal)] bg-[color:var(--charcoal)] text-white"
                                : "border-[color:var(--charcoal)]/20 hover:bg-[color:var(--charcoal)]/5"
                        }`}
                    >
                        <div
                            className="font-display italic text-3xl md:text-4xl leading-none"
                            data-testid={`users-count-${t.key}`}
                        >
                            {counts[t.key] ?? 0}
                        </div>
                        <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em] opacity-80">
                            {t.label}
                        </div>
                    </button>
                ))}
            </div>

            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <span className="font-mono text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                    {roleFilter === "all"
                        ? `${users.length} accounts`
                        : `${visibleUsers.length} ${
                              roleFilter === "super_admin" ? "super admin" : roleFilter
                          }${visibleUsers.length === 1 ? "" : "s"}`}
                </span>
                <div className="flex gap-2 flex-wrap">
                    <a
                        href={`${API}/admin/users/export.csv`}
                        onClick={async (e) => {
                            // Auth header can't be attached to a plain <a>; do
                            // a fetch + blob dance so the CSV downloads with
                            // the caller's token.
                            e.preventDefault();
                            try {
                                const res = await fetch(
                                    `${API}/admin/users/export.csv`,
                                    { headers },
                                );
                                if (!res.ok) throw new Error(await res.text());
                                const blob = await res.blob();
                                const link = document.createElement("a");
                                link.href = URL.createObjectURL(blob);
                                link.download =
                                    res.headers
                                        .get("content-disposition")
                                        ?.match(/filename="([^"]+)"/)?.[1] ||
                                    "equity-nation-users.csv";
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                            } catch (err) {
                                setError("CSV download failed.");
                            }
                        }}
                        data-testid="users-export-csv"
                        className="pill-btn ghost"
                        style={{ padding: "0.55rem 1rem" }}
                    >
                        Export CSV
                    </a>
                    <button
                        type="button"
                        onClick={() => setShowCreate((v) => !v)}
                        data-testid="users-toggle-create"
                        className="pill-btn"
                        style={{ padding: "0.55rem 1rem" }}
                    >
                        <Plus size={13} strokeWidth={1.7} />
                        {showCreate ? "Cancel" : "New account"}
                    </button>
                </div>
            </div>

            {showCreate && (
                <form
                    onSubmit={createUser}
                    data-testid="users-create-form"
                    className="border border-[color:var(--charcoal)] p-6 mb-8 bg-[color:var(--cloud)] grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                    {["email", "name", "nickname", "password"].map((k) => (
                        <label key={k} className="block">
                            <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                                {k}
                            </span>
                            <input
                                type={k === "password" ? "password" : k === "email" ? "email" : "text"}
                                required={k !== "nickname"}
                                minLength={k === "password" ? 8 : undefined}
                                value={form[k]}
                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                data-testid={`users-input-${k}`}
                                className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                            />
                        </label>
                    ))}
                    <label className="block">
                        <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                            Role
                        </span>
                        <select
                            value={form.role}
                            data-testid="users-input-role"
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                        >
                            <option value="member">member</option>
                            <option value="moderator">moderator</option>
                            {me?.role === "super_admin" && (
                                <option value="admin">admin</option>
                            )}
                        </select>
                    </label>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            data-testid="users-create-submit"
                            className="pill-btn"
                            style={{ padding: "0.55rem 1rem" }}
                        >
                            Create account
                        </button>
                    </div>
                </form>
            )}

            {error && (
                <div data-testid="users-error" className="mb-4 p-3 border border-[color:var(--charcoal)] font-body text-sm">
                    {error}
                </div>
            )}
            {loading ? (
                <div className="flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                    <Loader2 size={14} className="animate-spin" />
                    Loading
                </div>
            ) : (
                <ul className="divide-y divide-[color:var(--charcoal)]/15" data-testid="users-list">
                    {visibleUsers.length === 0 && (
                        <li className="py-8 text-center font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                            No {roleFilter === "all" ? "accounts" : roleFilter + "s"} yet.
                        </li>
                    )}
                    {visibleUsers.map((u) => (
                        <li
                            key={u.id}
                            data-testid={`user-row-${u.id}`}
                            className="py-5 grid grid-cols-12 gap-4 items-center"
                        >
                            <div className="col-span-12 md:col-span-6">
                                <div className="font-display italic text-lg">
                                    {u.name}
                                    <span className="ml-2 font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                        @{u.nickname || "—"}
                                    </span>
                                </div>
                                <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] flex items-center gap-3 flex-wrap">
                                    <span>{u.email}</span>
                                    <span
                                        data-testid={`user-subscribed-${u.id}`}
                                        title={
                                            u.stay_informed
                                                ? "Opted into Stay Informed"
                                                : "Not subscribed"
                                        }
                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono text-[11px] tracking-[0.18em] ${
                                            u.stay_informed
                                                ? "border-[color:var(--charcoal)] bg-[color:var(--charcoal)] text-white"
                                                : "border-[color:var(--charcoal)]/30 text-[color:var(--slate)]"
                                        }`}
                                    >
                                        {u.stay_informed ? "Subscribed" : "Not subscribed"}
                                    </span>
                                </div>
                            </div>
                            <div className="col-span-6 md:col-span-2">
                                {canActOn(u) ? (
                                    <select
                                        value={u.role}
                                        data-testid={`user-role-${u.id}`}
                                        onChange={(e) => changeRole(u.id, e.target.value)}
                                        className="bg-transparent border-b border-[color:var(--charcoal)]/40 outline-none py-1 font-mono text-[12px] uppercase tracking-[0.22em]"
                                    >
                                        <option value="member">member</option>
                                        <option value="moderator">moderator</option>
                                        {me?.role === "super_admin" && (
                                            <option value="admin">admin</option>
                                        )}
                                    </select>
                                ) : (
                                    <span
                                        data-testid={`user-role-${u.id}`}
                                        className="font-mono text-[12px] uppercase tracking-[0.22em]"
                                    >
                                        {u.role}
                                        {u.role === "super_admin" && " · founder"}
                                    </span>
                                )}
                            </div>
                            <div className="col-span-6 md:col-span-4 flex md:justify-end gap-2 flex-wrap">
                                {canActOn(u) && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    `Delete ${u.email}? This cannot be undone.`,
                                                )
                                            )
                                                deleteUser(u.id);
                                        }}
                                        data-testid={`user-delete-${u.id}`}
                                        className="pill-btn ghost"
                                        style={{ padding: "0.5rem 0.9rem" }}
                                    >
                                        <Trash2 size={12} strokeWidth={1.7} />
                                        Delete
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function CommentsTab({ token }) {
    const [statusFilter, setStatusFilter] = useState("pending");
    const [query, setQuery] = useState("");
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { status: statusFilter };
            if (query.trim()) params.q = query.trim();
            const [list, c] = await Promise.all([
                axios.get(`${API}/admin/comments`, { headers, params }),
                axios.get(`${API}/admin/comments/counts`, { headers }),
            ]);
            setItems(list.data || []);
            setCounts(c.data);
        } catch (e) {
            setError(formatError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [headers, statusFilter, query]);

    // Debounce the query input so typing doesn't hammer the API.
    useEffect(() => {
        const t = setTimeout(() => refresh(), 250);
        return () => clearTimeout(t);
    }, [refresh]);

    const act = async (id, action) => {
        try {
            if (action === "delete") await axios.delete(`${API}/admin/comments/${id}`, { headers });
            else
                await axios.post(
                    `${API}/admin/comments/${id}/${action}`,
                    {},
                    { headers },
                );
            setItems((p) => p.filter((it) => it.id !== id));
            refresh();
        } catch (e) {
            setError(formatError(e.response?.data?.detail));
        }
    };

    const tabs = [
        { id: "pending", label: `Pending · ${counts.pending}` },
        { id: "approved", label: `Approved · ${counts.approved}` },
        { id: "rejected", label: `Rejected · ${counts.rejected}` },
        { id: "all", label: `All · ${counts.total}` },
    ];

    return (
        <div data-testid="comments-tab">
            <div role="tablist" className="flex flex-wrap gap-2 mb-4 border-b border-[color:var(--charcoal)]/20 pb-3">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={statusFilter === t.id}
                        data-testid={`admin-tab-${t.id}`}
                        onClick={() => setStatusFilter(t.id)}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono text-[12px] uppercase tracking-[0.22em] border transition-colors ${
                            statusFilter === t.id
                                ? "bg-[color:var(--charcoal)] text-white border-[color:var(--charcoal)]"
                                : "border-[color:var(--charcoal)]/30 hover:bg-[color:var(--charcoal)]/5"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <div className="mb-8 flex items-center gap-3">
                <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name, email, or comment text…"
                    data-testid="comments-search"
                    aria-label="Search comments"
                    className="flex-1 max-w-md bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => setQuery("")}
                        data-testid="comments-search-clear"
                        className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] hover:text-[color:var(--charcoal)] transition-colors"
                    >
                        Clear
                    </button>
                )}
            </div>
            {error && (
                <div className="mb-6 p-3 border border-[color:var(--charcoal)] font-body text-sm">
                    {error}
                </div>
            )}
            {loading ? (
                <div className="flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                    <Loader2 size={14} className="animate-spin" />
                    Loading
                </div>
            ) : items.length === 0 ? (
                <p data-testid="admin-empty" className="font-body text-[color:var(--charcoal)]/80 italic">
                    Nothing to moderate right now.
                </p>
            ) : (
                <ul data-testid="admin-comments-list" className="divide-y divide-[color:var(--charcoal)]/15">
                    {items.map((c) => (
                        <li key={c.id} data-testid={`admin-comment-${c.id}`} className="py-6 md:py-8 grid grid-cols-12 gap-6 items-start">
                            <div className="col-span-12 md:col-span-8">
                                <div className="flex items-baseline gap-3 flex-wrap mb-2">
                                    <span className="font-display italic text-lg md:text-xl">{c.name}</span>
                                    <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">{c.email}</span>
                                    <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                        · {threadLabel[c.thread_id] || c.thread_id}
                                    </span>
                                    <span
                                        className={`font-mono text-[11px] uppercase tracking-[0.22em] px-2 py-0.5 border ${
                                            c.status === "approved"
                                                ? "border-[color:var(--charcoal)] bg-[color:var(--charcoal)] text-white"
                                                : c.status === "rejected"
                                                    ? "border-[color:var(--charcoal)]/40 text-[color:var(--slate)]"
                                                    : "border-[color:var(--charcoal)]"
                                        }`}
                                    >
                                        {c.status}
                                    </span>
                                </div>
                                <p className="font-body text-base leading-[1.6] text-[color:var(--charcoal)]/90 whitespace-pre-line">{c.body}</p>
                                <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mt-2">{formatDate(c.created_at)}</p>
                            </div>
                            <div className="col-span-12 md:col-span-4 flex md:justify-end gap-2 flex-wrap">
                                {c.status !== "approved" && (
                                    <button type="button" onClick={() => act(c.id, "approve")} data-testid={`admin-approve-${c.id}`} className="pill-btn" style={{ padding: "0.55rem 1rem" }}>
                                        <Check size={13} strokeWidth={1.7} />Approve
                                    </button>
                                )}
                                {c.status !== "rejected" && (
                                    <button type="button" onClick={() => act(c.id, "reject")} data-testid={`admin-reject-${c.id}`} className="pill-btn ghost" style={{ padding: "0.55rem 1rem" }}>
                                        <XCircle size={13} strokeWidth={1.7} />Reject
                                    </button>
                                )}
                                <button type="button" onClick={() => act(c.id, "delete")} data-testid={`admin-delete-${c.id}`} className="pill-btn ghost" style={{ padding: "0.55rem 1rem" }}>
                                    <Trash2 size={13} strokeWidth={1.7} />Delete
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function Admin() {
    const { user, token, loading, logout } = useAuth();
    const [authOpen, setAuthOpen] = useState(false);
    // If URL says ?edit=<slug>, jump straight to the Articles tab.
    const editSlug = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("edit")
        : null;
    const [tab, setTab] = useState(editSlug ? "articles" : "comments");

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--slate)] gap-2">
                <Loader2 size={14} className="animate-spin" />
                Loading
            </div>
        );
    }

    const hasAccess = user && roleRank[user.role] >= roleRank.moderator;

    if (!hasAccess) {
        return (
            <>
                <div className="min-h-screen flex items-center justify-center px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="w-full max-w-md border border-[color:var(--charcoal)] p-8 md:p-10 bg-[color:var(--cloud)]"
                    >
                        <div className="flex items-center gap-2 mb-6">
                            <ShieldCheck size={16} strokeWidth={1.6} />
                            <span className="font-mono text-[12px] uppercase tracking-[0.26em]">
                                Moderation panel
                            </span>
                        </div>
                        <h1 className="font-display text-[2rem] md:text-[2.7rem] leading-[0.95] tracking-[-0.02em] mb-4">
                            Sign in required
                        </h1>
                        <p className="font-body text-[color:var(--charcoal)]/80 mb-8">
                            {user
                                ? "This area is for moderators and admins only. If you should have access, contact the site founder."
                                : "Please sign in with your admin or moderator account."}
                        </p>
                    <div className="flex flex-col gap-3">
                        <button
                            type="button"
                            onClick={() => setAuthOpen(true)}
                            data-testid="admin-open-signin"
                            className="pill-btn w-full justify-center"
                        >
                            {user ? "Sign in as a different user" : "Sign in"}
                        </button>
                        <a
                            href="/"
                            data-testid="admin-gate-back-home"
                            className="pill-btn ghost w-full justify-center"
                        >
                            <ArrowLeft size={13} strokeWidth={1.6} />
                            Back to site
                        </a>
                        {user && (
                            <button
                                type="button"
                                onClick={logout}
                                data-testid="admin-signout-current"
                                className="pill-btn ghost w-full justify-center"
                            >
                                Sign out ({user.email})
                            </button>
                        )}
                    </div>
                    </motion.div>
                </div>
                <AuthModal
                    open={authOpen}
                    mode="login"
                    onClose={() => setAuthOpen(false)}
                />
            </>
        );
    }

    const canManageUsers = roleRank[user.role] >= roleRank.admin;
    const canManageArticles = roleRank[user.role] >= roleRank.admin;

    const tabs = [
        { id: "comments", label: "Moderation", icon: MessageSquare },
        { id: "stats", label: "Visitors", icon: BarChart3 },
        ...(canManageArticles ? [{ id: "articles", label: "Articles", icon: FileText }] : []),
        ...(canManageArticles ? [{ id: "home-copy", label: "Home Copy", icon: HomeIcon }] : []),
        ...(canManageUsers ? [{ id: "users", label: "Users", icon: UsersIcon }] : []),
    ];

    return (
        <div data-testid="admin-panel" className="min-h-screen px-6 md:px-10 py-16 md:py-20 max-w-[1440px] mx-auto">
            <header className="flex items-center justify-between flex-wrap gap-4 mb-10">
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <ShieldCheck size={14} strokeWidth={1.6} />
                        <span className="font-mono text-[12px] uppercase tracking-[0.26em]">
                            {user.role.replace("_", " ")} panel
                        </span>
                        <span
                            data-testid="admin-version"
                            title={`Deployed ${PUBLISHED_ON}`}
                            className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] border border-[color:var(--charcoal)]/25 px-2 py-0.5"
                        >
                            v{APP_VERSION} · {PUBLISHED_ON}
                        </span>
                    </div>
                    <h1 className="font-display text-[2rem] md:text-[2.7rem] leading-[0.95] tracking-[-0.02em]">
                        Hi, {user.nickname || user.name}.
                    </h1>
                    <p className="font-body text-[color:var(--charcoal)]/85 mt-2">{user.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <a
                        href="/"
                        data-testid="admin-back-home"
                        className="pill-btn ghost"
                        style={{ padding: "0.6rem 1rem" }}
                    >
                        <ArrowLeft size={13} strokeWidth={1.6} />
                        Back to site
                    </a>
                    <button type="button" onClick={logout} data-testid="admin-logout" className="pill-btn" style={{ padding: "0.6rem 1rem" }}>
                        <LogOut size={13} strokeWidth={1.6} />
                        Sign out
                    </button>
                </div>
            </header>

            <nav className="flex flex-wrap gap-2 mb-10 border-b border-[color:var(--charcoal)]/20 pb-3">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        data-testid={`admin-nav-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono text-[12px] uppercase tracking-[0.22em] border transition-colors ${
                            tab === t.id
                                ? "bg-[color:var(--charcoal)] text-white border-[color:var(--charcoal)]"
                                : "border-[color:var(--charcoal)]/30 hover:bg-[color:var(--charcoal)]/5"
                        }`}
                    >
                        <t.icon size={12} strokeWidth={1.7} />
                        {t.label}
                    </button>
                ))}
            </nav>

            {tab === "comments" && <CommentsTab token={token} />}
            {tab === "stats" && <StatsCard token={token} />}
            {tab === "articles" && canManageArticles && (
                <ArticlesTab
                    token={token}
                    initialEditSlug={editSlug}
                    onComposeNewsletter={() => setTab("home-copy")}
                />
            )}
            {tab === "home-copy" && canManageArticles && (
                <HomeCopyTab token={token} />
            )}
            {tab === "users" && canManageUsers && <UsersTab token={token} me={user} />}
        </div>
    );
}
